// Opt-in performance instrumentation. No-op unless explicitly enabled.
//
// Activation: ?perf=1 URL param, localStorage.__PERF__=1, or NEXT_PUBLIC_PERF=1
// at build time. When disabled, every export here returns immediately with
// negligible overhead.
//
// Usage guide: docs/testing/performance-profiling.md

type RenderRecord = {
  id: string
  phase: 'mount' | 'update' | 'nested-update'
  actualDuration: number
  baseDuration: number
  startTime: number
  commitTime: number
}

type MarkRecord = {
  name: string
  duration: number
  startTime: number
}

type PerfStore = {
  renders: RenderRecord[]
  marks: MarkRecord[]
  report: () => void
  clear: () => void
}

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    __PERF__?: boolean
    __perf?: PerfStore
  }
}

function checkUrlOrStorage(): boolean {
  if (typeof window === 'undefined') return false
  try {
    if (new URLSearchParams(window.location.search).get('perf') === '1') return true
    if (window.localStorage.getItem('__PERF__') === '1') return true
  } catch {
    // ignore
  }
  return false
}

export const isEnabled = () => {
  if (typeof window === 'undefined') return false
  if (window.__PERF__ === true) return true
  if (process.env.NEXT_PUBLIC_PERF === '1') return true
  if (checkUrlOrStorage()) {
    window.__PERF__ = true
    return true
  }
  return false
}

function ensureStore() {
  if (typeof window === 'undefined') return null
  if (!window.__perf) {
    window.__perf = {
      renders: [],
      marks: [],
      report,
      clear,
    }
  }
  return window.__perf
}

export function markStart(name: string) {
  if (!isEnabled()) return
  try {
    performance.mark(`${name}:start`)
  } catch {
    // perf API unavailable; ignore
  }
}

export function markEnd(name: string) {
  if (!isEnabled()) return
  try {
    performance.mark(`${name}:end`)
    const measure = performance.measure(name, `${name}:start`, `${name}:end`)
    const store = ensureStore()
    if (store) {
      store.marks.push({
        name,
        duration: measure.duration,
        startTime: measure.startTime,
      })
    }
  } catch {
    // perf API unavailable; ignore
  }
}

export function measureSync<T>(name: string, fn: () => T): T {
  if (!isEnabled()) return fn()
  markStart(name)
  try {
    return fn()
  } finally {
    markEnd(name)
  }
}

export function recordRender(
  id: string,
  phase: 'mount' | 'update' | 'nested-update',
  actualDuration: number,
  baseDuration: number,
  startTime: number,
  commitTime: number,
) {
  if (!isEnabled()) return
  const store = ensureStore()
  if (!store) return
  store.renders.push({
    id,
    phase,
    actualDuration,
    baseDuration,
    startTime,
    commitTime,
  })
}

function report() {
  if (typeof window === 'undefined' || !window.__perf) {
    console.log('[perf] not enabled. Run window.__PERF__ = true and reload.')
    return
  }
  const store = window.__perf

  // Aggregate marks by name
  const markAgg = new Map<string, { count: number; total: number; max: number; min: number }>()
  for (const m of store.marks) {
    const cur = markAgg.get(m.name) ?? {
      count: 0,
      total: 0,
      max: -Infinity,
      min: Infinity,
    }
    cur.count++
    cur.total += m.duration
    cur.max = Math.max(cur.max, m.duration)
    cur.min = Math.min(cur.min, m.duration)
    markAgg.set(m.name, cur)
  }

  // Aggregate renders by id+phase
  const renderAgg = new Map<
    string,
    { count: number; totalActual: number; totalBase: number; maxActual: number }
  >()
  for (const r of store.renders) {
    const key = `${r.id} (${r.phase})`
    const cur = renderAgg.get(key) ?? {
      count: 0,
      totalActual: 0,
      totalBase: 0,
      maxActual: -Infinity,
    }
    cur.count++
    cur.totalActual += r.actualDuration
    cur.totalBase += r.baseDuration
    cur.maxActual = Math.max(cur.maxActual, r.actualDuration)
    renderAgg.set(key, cur)
  }

  console.log('[perf] === Marks (named measurements) ===')
  const markRows = Array.from(markAgg.entries())
    .map(([name, v]) => ({
      name,
      count: v.count,
      total_ms: round(v.total),
      avg_ms: round(v.total / v.count),
      max_ms: round(v.max),
    }))
    .sort((a, b) => b.total_ms - a.total_ms)

  console.table(markRows)

  console.log('[perf] === React Profiler renders ===')
  const renderRows = Array.from(renderAgg.entries())
    .map(([key, v]) => ({
      tree: key,
      renders: v.count,
      total_actual_ms: round(v.totalActual),
      avg_actual_ms: round(v.totalActual / v.count),
      max_actual_ms: round(v.maxActual),
      total_base_ms: round(v.totalBase),
    }))
    .sort((a, b) => b.total_actual_ms - a.total_actual_ms)

  console.table(renderRows)

  console.log(`[perf] totals: ${store.marks.length} marks, ${store.renders.length} render entries.`)
}

function clear() {
  if (typeof window === 'undefined' || !window.__perf) return
  window.__perf.renders = []
  window.__perf.marks = []
  try {
    performance.clearMarks()
    performance.clearMeasures()
  } catch {
    // perf API unavailable; ignore
  }
  console.log('[perf] cleared')
}

function round(n: number) {
  return Math.round(n * 100) / 100
}
